import React, { ReactElement, useState, useEffect, useCallback } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Modal, Button, Space } from 'antd';
import CcxComponentProps from '../../core/CcxComponent';
import { billingDisabled, StripePK } from '../../core/CcxEnv';
import useCards from '../../core/hooks/useCards';
import AddCard from '../account/AddCard';

interface Props extends CcxComponentProps {
    children: ReactElement;
    onAction: () => void | Promise<void>;
    actionText?: string;
    title?: string;
    description?: string;
    disabled?: boolean;
    includePromoCode?: boolean;
}

/**
 * Wrapper component that checks if user has credit cards before allowing certain actions.
 * If no cards exist, it shows the AddCard modal with integrated promo code functionality.
 */
function CreditCardRequiredWrapper({
    children,
    onAction,
    actionText = 'Continue',
    title = 'Credit Card Required',
    description = 'A valid credit card is required for this action. Please add one to continue.',
    disabled = false,
    includePromoCode = true,
    testId = 'CreditCardRequiredWrapper',
}: Props): ReactElement {
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previousHasValidCards, setPreviousHasValidCards] = useState(false);

    const { cards, refresh, loading: cardsLoading } = useCards();
    const stripePromise = !billingDisabled ? loadStripe(StripePK) : null;

    const hasValidCards = Boolean(
        cards && cards.length > 0 && cards.some((card) => card.isValid())
    );

    const executeAction = useCallback(async () => {
        setLoading(true);
        try {
            await onAction();
        } finally {
            setLoading(false);
        }
    }, [onAction]);

    // Track when valid cards state changes from false to true (successful card addition)
    useEffect(() => {
        if (!previousHasValidCards && hasValidCards && showModal) {
            // Successfully added a valid card, close modal and execute action
            setShowModal(false);
            executeAction();
        }
    }, [hasValidCards, previousHasValidCards, showModal, executeAction]);

    const handleChildClick = async () => {
        if (billingDisabled) {
            // If billing is disabled, proceed with action
            await executeAction();
            return;
        }

        if (hasValidCards) {
            // User has valid cards, proceed with action
            await executeAction();
        } else {
            // Show modal to add card first
            setPreviousHasValidCards(hasValidCards);
            setShowModal(true);
        }
    };

    const handleCardAdded = async () => {
        // Refresh cards list - the useEffect will handle success detection
        await refresh();
    };

    const handleCancel = () => {
        setShowModal(false);
        setPreviousHasValidCards(false);
    };

    // Clone the child element and add onClick handler
    const clonedChild = React.cloneElement(children, {
        onClick: handleChildClick,
        loading: loading || cardsLoading,
        disabled: disabled || loading || cardsLoading,
    });

    return (
        <>
            {clonedChild}
            <Modal
                title={title}
                open={showModal}
                onCancel={handleCancel}
                width={500}
                footer={
                    <Space size={16}>
                        <Button key="cancel" onClick={handleCancel}>
                            Cancel
                        </Button>
                        {stripePromise && (
                            <Elements key="stripe" stripe={stripePromise}>
                                <AddCard
                                    onSuccess={handleCardAdded}
                                    submitText="Add Card & Continue"
                                    includePromoCode={includePromoCode}
                                    testId={`${testId}AddCard`}
                                    buttonType="primary"
                                />
                            </Elements>
                        )}
                    </Space>
                }
                data-testid={`${testId}Modal`}
            >
                <div style={{ marginBottom: 16 }}>{description}</div>
            </Modal>
        </>
    );
}

export default CreditCardRequiredWrapper;
